import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { getInvoiceDraftEditRoute } from '@features/invoices/utils/getInvoiceDraftEditRoute'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useDirtyRoute } from '../../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { invoiceEditorFromQuoteVariants } from '../constants/variants/invoiceEditorFromQuoteVariants'
import { invoiceEditorVariantToBillingStage } from '../constants/variants/invoiceEditorVariantToBillingStage'
import { useInvoiceEditorForm } from '../contexts/invoiceEditorFormContext'
import { InvoiceEditorVariant } from '../enums/invoiceEditorVariant'
import { getDocumentDraftCreationNotification } from '../utils/getDocumentDraftCreationNotification'
import { useInvoiceEditorDocumentType } from './useInvoiceEditorDocumentType'
import { useInvoiceEditorVariant } from './useInvoiceEditorVariant'
import { useSaveInvoiceDraft } from './useSaveInvoiceDraft'

const variantsRedirectedToDraftEdit = [
  InvoiceEditorVariant.InvoiceDraftDuplicate,
  InvoiceEditorVariant.InvoiceNew,
  InvoiceEditorVariant.InvoiceDuplicate,
  ...invoiceEditorFromQuoteVariants,
  InvoiceEditorVariant.QuoteNew,
  InvoiceEditorVariant.QuoteDuplicate,
  InvoiceEditorVariant.QuoteDraftDuplicate,
]

export const useSaveInvoiceAsDraft = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setDirty } = useDirtyRoute()
  const { organization } = useUserOrganization()
  const { saveInvoiceDraft, isProcessing, isLoading } = useSaveInvoiceDraft()
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()
  const { documentType } = useInvoiceEditorDocumentType()

  const shouldRedirectToDraftEdit = variantsRedirectedToDraftEdit.includes(invoiceEditorVariant)

  const { getValues } = invoiceEditorForm

  const saveAsDraft = useCallback(() => {
    const formData = getValues()

    saveInvoiceDraft({
      formData,
      onSuccess: (draftId) => {
        const successNotification = getDocumentDraftCreationNotification(documentType, t)

        setDirty(false)

        if (shouldRedirectToDraftEdit) {
          const billingStage = invoiceEditorVariantToBillingStage[invoiceEditorVariant]
          const editDraftRoute = getInvoiceDraftEditRoute(billingStage, String(draftId), documentType)

          navigate(`${getOrganizationViewUrl(organization?.url, editDraftRoute)}`)
        }

        notify({
          id: NotificationKeys.ExternalInvoiceSavedAsDraft,
          message: successNotification,
          variant: 'success',
        })
      },
    })
  }, [
    documentType,
    getValues,
    invoiceEditorVariant,
    navigate,
    organization?.url,
    saveInvoiceDraft,
    setDirty,
    shouldRedirectToDraftEdit,
    t,
  ])

  return { saveAsDraft, isLoading, isSaving: isProcessing }
}
